import { useNavigate } from "@solidjs/router";
import { CreateProjectRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent } from "@storytell/events";
import type { ProjectWithInvites } from "@storytell/types";
import type { Role as ServerRole } from "@storytell/types";
import { AnimateStaggeredChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { StDialog, StDialogCloseButton, StDialogTitle } from "@storytell/ui/dialog";
import {
  TbArrowLeft,
  TbBoxMultiple,
  TbHelpCircle,
  TbLink,
  TbLock,
  TbMail,
  TbPlus,
  TbSearch,
  TbUsers,
  TbWorld,
  TbX,
} from "solid-icons/tb";
import { type Component, For, Show, createSignal, onCleanup, onMount } from "solid-js";
import { isFreeEmailDomain } from "~/utils/free-email-domains";
import { useWire } from "~/wire";
import { TagInput } from "./TagInput";

interface CreateProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialView?: ViewState;
}

type ViewState = "grid" | "form";
type Role = "Owner" | "Collaborator" | "Reader";

// Map project permissions to user role
const mapPermissionsToRole = (canRead: boolean, canUpdate: boolean, canDelete: boolean, canCreate: boolean): Role => {
  // If only can read, it's a Reader role
  if (canRead && !canUpdate && !canDelete && !canCreate) {
    return "Reader";
  }

  // If can do everything, it's an Owner role
  if (canRead && canUpdate && canDelete && canCreate) {
    return "Owner";
  }

  // If can do everything but delete, it's a Collaborator role
  if (canRead && canUpdate && canCreate && !canDelete) {
    return "Collaborator";
  }

  // Default fallback - if they have some permissions but don't fit the above patterns,
  // we'll default to Collaborator as it's the most permissive non-owner role
  return "Collaborator";
};

interface Project {
  id: string;
  name: string;
  description: string;
  isPrivate: boolean;
}

interface EmailInvite {
  id: string;
  email: string;
  role: Role;
}

interface DomainInvite {
  id: string;
  domain: string;
  role: Role;
}

// Map UI roles to backend role format
const mapRoleToBackend = (role: Role): "role.owner" | "role.collaborator" | "role.reader" => {
  switch (role) {
    case "Owner":
      return "role.owner";
    case "Collaborator":
      return "role.collaborator";
    case "Reader":
      return "role.reader";
  }
};

const mapServerRoleToUi = (role: ServerRole): Role => {
  switch (role) {
    case "role.owner":
      return "Owner";
    case "role.collaborator":
      return "Collaborator";
    case "role.reader":
      return "Reader";
  }
};

interface ProjectsGridViewProps {
  onCreateProject: () => void;
  onClose: () => void;
}

const ProjectsGridView: Component<ProjectsGridViewProps> = (props) => {
  const wire = useWire();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = createSignal("");

  const activeProjectId = () => wire.userContext.get()?.projectId;

  const activeProject = () => {
    const activeProjId = activeProjectId();
    return wire.projects.all().find((p) => p.id === activeProjId);
  };

  const allProjects = () => {
    const activeProjId = activeProjectId();
    return wire.projects.all().filter((p) => p.id !== activeProjId);
  };

  const filteredProjects = () => {
    const query = searchQuery().toLowerCase().trim();
    if (!query) return allProjects();

    return allProjects().filter((project) => {
      const nameMatch = project.name.toLowerCase().includes(query);
      const descriptionMatch = project.description?.toLowerCase().includes(query) ?? false;
      return nameMatch || descriptionMatch;
    });
  };

  return (
    <>
      <div class="mb-2">
        <StDialogTitle class="text-xl font-semibold bg-gradient-to-r from-brand via-brand/80 to-brand bg-clip-text text-transparent flex items-center gap-2">
          <TbBoxMultiple class="w-5 h-5 text-brand" />
          Projects
        </StDialogTitle>
      </div>

      <p class="text-sm text-muted-text mb-6">
        Create shared projects so you can <span class="font-semibold">talk to your data</span>, together.
        <br /> Collaborate with your colleagues, team, company, clients or community.
      </p>
      <div class="space-y-4 max-h-[calc(80vh-120px)] overflow-y-auto pr-2">
        {/* Active Project Card - Full Width */}
        <Show when={activeProject()}>
          {(project) => {
            return (
              <div class="relative p-6 border-2 border-brand/40 rounded-xl bg-gradient-to-br from-canvas to-brand/5 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-brand/60 group">
                <div class="absolute inset-0 bg-gradient-to-br from-brand/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div class="relative flex items-start justify-between gap-4">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-2">
                      <h3 class="font-semibold line-clamp-1 text-primary-text">{project().name}</h3>
                      <span class="px-2 py-0.5 text-xs font-medium bg-brand/20 text-brand rounded-full shrink-0 border border-brand/30">
                        Active
                      </span>
                      <span class="px-2 py-0.5 text-xs font-medium bg-muted/20 text-muted-text rounded-full shrink-0">
                        {mapServerRoleToUi(project().role)}
                      </span>
                    </div>
                    <p class="text-sm text-muted-text line-clamp-2 mb-2">{project().description}</p>
                    <Show when={project().createdBy}>
                      <p class="text-xs text-muted-text">
                        Created by:{" "}
                        <Show
                          when={project().createdBy === wire.auth.userId()}
                          fallback={
                            <span class="font-mono text-xs">
                              {project().createdByName === "Created by Storytell"
                                ? "Storytell"
                                : project().createdByName}
                            </span>
                          }
                        >
                          <span class="font-medium">You</span>
                        </Show>
                      </p>
                    </Show>
                  </div>
                  <StButton
                    onClick={() => {
                      navigate(`/projects/${project().id}`);
                      props.onClose();
                    }}
                    class="shrink-0 text-sm bg-brand text-on-brand hover:bg-brand-hover transition-all duration-200 hover:shadow-md"
                  >
                    Manage project
                  </StButton>
                </div>
              </div>
            );
          }}
        </Show>

        {/* Search Bar */}
        <div class="relative">
          <div class="relative">
            <TbSearch class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-text" />
            <input
              type="text"
              placeholder="Search projects by name or description..."
              value={searchQuery()}
              onInput={(e) => setSearchQuery(e.currentTarget.value)}
              class="w-[calc(100%-0.5rem)] pl-10 pr-4 py-2.5 bg-canvas border border-primary-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-border focus:border-primary-border transition-colors"
            />
          </div>
        </div>

        {/* Projects Grid - Add Project Button + Other Projects */}
        <AnimateStaggeredChildren
          show={true}
          enter={{ opacity: [0, 1], y: [20, 0] }}
          exit={{ opacity: [1, 0], y: [0, -20] }}
          duration={300}
          stagger={50}
        >
          {(anim) => (
            <div ref={anim.ref} class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Add Project Button */}
              <button
                type="button"
                onClick={props.onCreateProject}
                class="min-h-[140px] p-4 border-2 border-dashed border-brand rounded-xl hover:border-brand hover:bg-gradient-to-br hover:from-brand/10 hover:to-brand/5 hover:shadow-lg hover:shadow-brand/20 transition-all duration-300 flex items-center justify-center gap-3 cursor-pointer group relative overflow-hidden"
              >
                <div class="absolute inset-0 bg-gradient-to-br from-brand/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div class="relative flex items-center gap-3">
                  <div class="p-2 rounded-lg bg-brand/10 group-hover:bg-brand/20 transition-colors duration-300">
                    <TbPlus class="w-5 h-5 text-brand group-hover:scale-110 group-hover:rotate-90 transition-all duration-300" />
                  </div>
                  <span class="text-brand font-medium text-lg group-hover:text-brand-hover transition-colors duration-300">
                    Add Project
                  </span>
                </div>
              </button>

              {/* Other Projects */}
              <For each={filteredProjects()}>
                {(project) => {
                  const userRole = () => mapServerRoleToUi(project.role);

                  const handleProjectClick = async () => {
                    const userContext = wire.userContext.get();
                    if (userContext) {
                      try {
                        await wire.userContext.switchContext({
                          tenantId: userContext.tenantId,
                          organizationId: userContext.organizationId,
                          workspaceId: userContext.workspaceId,
                          projectId: project.id,
                        });
                        props.onClose();
                      } catch (err) {
                        console.error("Failed to switch project", err);
                      }
                    }
                  };

                  const handleManageClick = (e: MouseEvent) => {
                    e.stopPropagation();
                    navigate(`/projects/${project.id}`);
                    props.onClose();
                  };

                  return (
                    <AnimateStaggeredChildren
                      show={true}
                      enter={{ opacity: [0, 1], y: [20, 0] }}
                      exit={{ opacity: [1, 0], y: [0, -20] }}
                      duration={300}
                      stagger={50}
                    >
                      {(cardAnim) => (
                        <button
                          ref={cardAnim.ref}
                          type="button"
                          onClick={handleProjectClick}
                          class="relative min-h-[140px] p-4 border border-primary-border rounded-xl bg-canvas hover:border-brand/30 hover:bg-gradient-to-br hover:from-canvas-hover hover:to-canvas hover:shadow-md transition-all duration-300 cursor-pointer text-left group overflow-hidden"
                        >
                          <div class="absolute inset-0 bg-gradient-to-br from-brand/0 to-brand/0 group-hover:from-brand/5 group-hover:to-transparent rounded-xl transition-all duration-300" />
                          <div class="relative flex flex-col h-full">
                            <div class="flex items-start justify-between gap-2 mb-2">
                              <div class="flex-1 min-w-0">
                                <h3 class="font-semibold line-clamp-1 text-primary-text group-hover:text-brand transition-colors duration-300">
                                  {project.name}
                                </h3>
                                <span class="py-0.5 px-2 text-xs font-medium bg-muted/20 text-muted-text rounded-full mt-1 inline-block group-hover:bg-brand/10 group-hover:text-brand transition-colors duration-300">
                                  {userRole()}
                                </span>
                              </div>
                            </div>
                            <p class="text-sm text-muted-text line-clamp-2 mb-2">{project.description}</p>
                            <Show when={project.createdBy}>
                              <p class="text-xs text-muted-text">
                                Created by:{" "}
                                <Show
                                  when={project.createdBy === wire.auth.userId()}
                                  fallback={
                                    <span class="font-mono text-xs">
                                      {project.createdByName === "Created by Storytell"
                                        ? "Storytell"
                                        : project.createdByName}
                                    </span>
                                  }
                                >
                                  <span class="font-medium">You</span>
                                </Show>
                              </p>
                            </Show>
                          </div>
                        </button>
                      )}
                    </AnimateStaggeredChildren>
                  );
                }}
              </For>
            </div>
          )}
        </AnimateStaggeredChildren>
      </div>
    </>
  );
};

interface CreateProjectFormViewProps {
  onBack: () => void;
  onSuccess: (project: ProjectWithInvites) => void;
}

const CreateProjectFormView: Component<CreateProjectFormViewProps> = (props) => {
  const wire = useWire();
  const client = useStorytellClient();

  // Form state
  const [projectName, setProjectName] = createSignal("");
  const [description, setDescription] = createSignal("");

  const handleSubmit = async (e: Event) => {
    e.preventDefault();

    const request = CreateProjectRequestConfig.withBearerToken(wire.auth.token());
    const response = await client.fetch(request, undefined, {
      name: projectName(),
      description: description(),
    });

    if (response.isErr()) {
      console.error("Failed to create project", response.error);
      // TODO: Show error to user
      return;
    }

    // Add the new project to the local store
    const newProject = response.value;
    wire.projects.set(newProject.id, newProject);

    // Refresh the projects list to ensure we have the latest data
    wire.projects.refetch();

    // Switch to the newly created project
    await wire.userContext.switchContext({
      tenantId: newProject.tenantId,
      organizationId: newProject.organizationId,
      workspaceId: newProject.workspaceId,
      projectId: newProject.id,
    });

    // Notify parent of success
    props.onSuccess(newProject);
  };

  return (
    <div class="flex flex-col h-full min-h-0">
      <div class="mb-4">
        <StButton
          onClick={props.onBack}
          class="flex items-center gap-1 text-sm text-muted-text hover:text-primary-text"
        >
          <TbArrowLeft class="w-4 h-4" />
          Back
        </StButton>
      </div>

      <StDialogTitle class="text-xl font-semibold mb-2">Create Project</StDialogTitle>
      <p class="text-sm text-muted-text mb-6">Invite people to collaborate with you</p>

      <div class="flex-1 overflow-y-auto min-h-0 p-0.5">
        <form onSubmit={handleSubmit} class="space-y-6">
          {/* Project Details */}
          <div class="space-y-4">
            <div>
              <div class="flex items-center justify-between mb-1.5">
                <label for="project-name" class="text-sm font-medium">
                  Project Name
                </label>
                <a
                  class="inline-flex items-center gap-1 text-xs text-brand underline hover:underline-offset-2"
                  href="https://go.storytell.ai/learn-projects"
                  target="_blank"
                  rel="noreferrer"
                >
                  <TbHelpCircle class="w-3.5 h-3.5" />
                  Learn about Projects
                </a>
              </div>
              <input
                id="project-name"
                type="text"
                value={projectName()}
                onInput={(e) => setProjectName(e.currentTarget.value)}
                placeholder="My Current Project"
                class="w-full px-3 py-2 bg-canvas border border-primary-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                required
              />
            </div>

            <div>
              <label for="project-description" class="block text-sm font-medium mb-1.5">
                Description <span class="text-muted-text font-normal">(optional)</span>
              </label>
              <textarea
                id="project-description"
                value={description()}
                onInput={(e) => setDescription(e.currentTarget.value)}
                placeholder="What is this project about?"
                rows={3}
                class="w-full px-3 py-2 bg-canvas border border-primary-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand resize-none"
              />
            </div>
          </div>
        </form>
      </div>

      {/* Fixed Footer */}
      <div class="flex items-center gap-3 pt-4 border-t border-primary-border bg-background">
        <StButton type="button" onClick={props.onBack} class="flex-1">
          Cancel
        </StButton>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!projectName().trim()}
          class="flex-1 px-4 py-2 bg-brand text-on-brand hover:bg-brand-hover border border-primary-border rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Create Project
        </button>
      </div>
    </div>
  );
};

export const ProjectSwitcherModal: Component<CreateProjectModalProps> = (props) => {
  const [view, setView] = createSignal<ViewState>(props.initialView || "grid");

  const handleSuccess = (_project: ProjectWithInvites) => {
    setView("grid");
    props.onOpenChange(false);
  };

  const handleModalChange = (open: boolean) => {
    if (!open) {
      setView("grid");
    }
    props.onOpenChange(open);
  };

  onMount(() => {
    const unsubscribeUserContextChanged = subscribeToGlobalEvent("user-context-changed", () => {
      handleModalChange(false);
    });

    onCleanup(() => {
      unsubscribeUserContextChanged();
    });
  });

  return (
    <StDialog
      open={props.open}
      onOpenChange={handleModalChange}
      contentProps={{
        class: view() === "grid" ? "max-w-3xl" : "max-w-2xl flex flex-col max-h-[80vh]",
      }}
      content={
        <>
          <StDialogCloseButton as={StButton} class="absolute top-4 right-4 z-10">
            <TbX />
          </StDialogCloseButton>

          <Show
            when={view() === "grid"}
            fallback={<CreateProjectFormView onBack={() => setView("grid")} onSuccess={handleSuccess} />}
          >
            <ProjectsGridView onCreateProject={() => setView("form")} onClose={() => props.onOpenChange(false)} />
          </Show>
        </>
      }
    />
  );
};
